import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import queryString from 'query-string';

import { ENDPOINTS } from '@/configs/endpoints';
import { DocumentSearchItem, DocumentSearchItemApi, documentSearchItemSchema } from '@/entities/DocumentsSearch';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { FieldDateRangeStore } from '@/stores/local/shared/form/FieldDateRangeStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import call from '@/utils/api';
import { type DocumentSearchSortBy, sortDocumentSearchItems } from '@/utils/sortDocumentSearchItems';

import { SearchSelectionStore } from '../../shared/SearchSelectionStore';

import { DocumentTypesStore } from './DocumentTypesStore';

type SearchDocumentStoreParams = {
  getOrganizationId: () => number;
};

export class SearchDocumentStore implements ILocalStore {
  private readonly _getOrganizationId: () => number;
  private readonly _searchValueDisposer: () => void;
  private readonly _periodDisposer: () => void;

  readonly meta = new MetaStore();
  readonly collection = new CollectionStore<DocumentSearchItem, DocumentSearchItemApi>({
    parseItem: (item) => documentSearchItemSchema.parse(item),
    getEntityKey: (item) => {
      return item.searchResultId;
    },
  });
  readonly searchField = new FieldTextStore();
  readonly selectionStore: SearchSelectionStore;
  readonly documentTypesStore: DocumentTypesStore;
  readonly periodStore = new FieldDateRangeStore();

  totalDocuments = 0;
  sortBy: DocumentSearchSortBy = 'relevance';

  get sortedItems() {
    return sortDocumentSearchItems(this.collection.items, this.sortBy);
  }

  constructor({ getOrganizationId }: SearchDocumentStoreParams) {
    this._getOrganizationId = getOrganizationId;
    this.documentTypesStore = new DocumentTypesStore({ organizationId: getOrganizationId() });
    this.selectionStore = new SearchSelectionStore({
      getVisibleIds: () => new Set(this.collection.items.map((item) => item.id)),
    });

    makeObservable(this, {
      totalDocuments: observable,
      sortBy: observable,
      sortedItems: computed,
      searchQuery: computed,
      hasActiveSearch: computed,
      hasActiveFilters: computed,
      activeFiltersCount: computed,
      canSubmitSearch: computed,
      isShowEmptySearchState: computed,
      isShowSearchContentLoaded: computed,
      isShowSearchContentWithError: computed,
      fetchDocumentsSearch: action.bound,
      reset: action.bound,
      resetFilters: action.bound,
      clearResults: action.bound,
      changeOrganizationId: action.bound,
      toggleDocumentType: action.bound,
      setSortBy: action.bound,
    });

    this._searchValueDisposer = reaction(
      () => this.searchField.value,
      (value) => {
        if (!value || value.trim().length === 0) {
          this.clearResults();
        }
      },
    );

    this._periodDisposer = reaction(
      () => this.periodStore.dateRangeValue,
      () => {
        if (this.hasActiveSearch) {
          this.fetchDocumentsSearch();
        }
      },
    );
  }

  changeOrganizationId(): void {
    this.reset();
  }

  reset() {
    this.searchField.reset();
    this.sortBy = 'relevance';
    this.resetFilters();
    this.clearResults();
  }

  get searchQuery(): string {
    return this.searchField.serverFormat?.trim() ?? '';
  }

  get hasActiveSearch(): boolean {
    return this.searchQuery.length > 0;
  }

  get hasActiveFilters(): boolean {
    return this.documentTypesStore.hasSelection || this.periodStore.hasFilter;
  }

  get activeFiltersCount(): number {
    let count = 0;

    if (this.documentTypesStore.hasSelection) {
      count += 1;
    }

    if (this.periodStore.hasFilter) {
      count += 1;
    }

    return count;
  }

  get canSubmitSearch(): boolean {
    return this.hasActiveSearch && !this.meta.isLoading;
  }

  get isShowEmptySearchState(): boolean {
    return this.hasActiveSearch && this.collection.items.length === 0 && this.meta.isLoaded;
  }

  get isShowSearchContentLoaded(): boolean {
    return this.hasActiveSearch && this.collection.items.length > 0 && this.meta.isLoaded;
  }

  get isShowSearchContentWithError(): boolean {
    return this.hasActiveSearch && (this.meta.isLoaded || this.meta.isError);
  }

  setSortBy(sortBy: DocumentSearchSortBy): void {
    this.sortBy = sortBy;
  }

  toggleDocumentType(key: string): void {
    if (key === DocumentTypesStore.ALL_KEY) {
      this.documentTypesStore.reset();
    } else {
      this.documentTypesStore.toggle(key);
    }

    if (this.hasActiveSearch) {
      this.fetchDocumentsSearch();
    }
  }

  resetFilters(): void {
    const hadPeriodFilter = this.periodStore.hasFilter;

    this.documentTypesStore.reset();
    this.periodStore.reset();

    if (this.hasActiveSearch && !hadPeriodFilter) {
      this.fetchDocumentsSearch();
    }
  }

  fetchDocumentsSearch = async (): Promise<void> => {
    if (!this.hasActiveSearch) {
      this.clearResults();

      return;
    }

    if (this.meta.isLoading) {
      return;
    }

    this.collection.clear();

    this.meta.setLoadedStartMeta();

    const organizationId = this._getOrganizationId();

    const typeIds = this.documentTypesStore.hasSelection ? this.documentTypesStore.selectedIds : undefined;

    const response = await call<{ items: DocumentSearchItemApi[]; total: number }>({
      endpoint: ENDPOINTS.documents.search(String(organizationId)),
      method: 'POST',
      config: {
        params: {
          search: this.searchQuery,
          result_count: 10,
          document_type_ids: typeIds,
          period_start: this.periodStore.start ?? undefined,
          period_end: this.periodStore.end ?? undefined,
        },
        paramsSerializer: {
          serialize: (params) => queryString.stringify(params, { skipNull: true }),
        },
      },
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      this.collection.clear();
      this.collection.pushRaws(response.data.items);
      this.totalDocuments = response.data.total;
      this.meta.setLoadedSuccessMeta();
      this.selectionStore.fillVisibleAsSelected();
    });
  };

  clearResults(): void {
    this.collection.clear();
    this.selectionStore.clear();
    this.totalDocuments = 0;
    this.meta.reset();
  }

  destroy(): void {
    this.collection.destroy();
    this.meta.destroy();
    this.searchField.destroy();
    this.selectionStore.destroy();
    this.documentTypesStore.destroy();
    this.periodStore.destroy();
    this._searchValueDisposer();
    this._periodDisposer();
  }
}
